import React, { useEffect, useRef } from 'react';
import './InteractiveBackground.css';

const InteractiveBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    let wordsArray = [];
    let shrapnelArray = [];
    
    const mouse = {
      x: null,
      y: null,
      radius: 120 // Reduced radius slightly so it breaks when fairly close
    };

    const handleMouseMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
    };
    
    const handleMouseOut = () => {
      mouse.x = null;
      mouse.y = null;
    };
    
    // Track DOM elements to fade out particles behind text
    let obstacles = [];
    const updateObstacles = () => {
      obstacles = [];
      const elements = document.querySelectorAll('p, h1, h2, h3, h4, span.auto-typing, .card, img, .social-links, .footer');
      elements.forEach(el => {
        let rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          obstacles.push(rect);
        }
      });
    };
    
    // Update obstacle bounds periodically and on scroll/resize
    const obstacleInterval = setInterval(updateObstacles, 500);
    window.addEventListener('scroll', updateObstacles, { passive: true });
    
    const handleTouchOrClick = (event) => {
      let touchX = event.clientX;
      let touchY = event.clientY;
      
      if (event.touches && event.touches.length > 0) {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      }
      
      // Briefly set mouse position to repel nearby particles
      mouse.x = touchX;
      mouse.y = touchY;

      // Shatter words near the tap
      for (let i = wordsArray.length - 1; i >= 0; i--) {
        let word = wordsArray[i];
        let textWidth = ctx.measureText(word.text).width;
        let dx = touchX - (word.x + textWidth / 2);
        let dy = touchY - word.y;
        let distance = Math.sqrt(dx * dx + dy * dy);
        
        // Generous hit area for mobile tapping (80px radius)
        if (distance < 80) {
          word.explode();
          wordsArray.splice(i, 1);
          spawnWord();
        }
      }
      
      // On mobile touch, clear the 'mouse' coordinates shortly after tap so things don't stay repelled
      if (event.type === 'touchstart') {
        setTimeout(() => {
          mouse.x = null;
          mouse.y = null;
        }, 500);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseOut);
    window.addEventListener('click', handleTouchOrClick);
    window.addEventListener('touchstart', handleTouchOrClick, {passive: true});
    
    const syntaxWords = [
      // JavaScript / General
      { text: "const", color: "#d73a49" },
      { text: "function", color: "#d73a49" },
      { text: "return", color: "#d73a49" },
      { text: "import", color: "#d73a49" },
      { text: "=>", color: "#005cc5" },
      { text: "{}", color: "#6a737d" },
      { text: "true", color: "#005cc5" },
      { text: "false", color: "#005cc5" },
      { text: "null", color: "#005cc5" },
      { text: "console.log", color: "#6f42c1" },
      { text: "React", color: "#6f42c1" },
      { text: "Node", color: "#22863a" },
      { text: "Docker", color: "#005cc5" },
      { text: "await", color: "#d73a49" },
      { text: "async", color: "#d73a49" },
      
      // Python
      { text: "def", color: "#d73a49" },
      { text: "class", color: "#d73a49" },
      { text: "Python", color: "#005cc5" },
      
      // SQL
      { text: "SELECT", color: "#22863a" },
      { text: "FROM", color: "#22863a" },
      
      // Go
      { text: "func", color: "#d73a49" },
      { text: "goroutine", color: "#6f42c1" },
      { text: "chan", color: "#22863a" },
      { text: "defer", color: "#d73a49" },
      { text: "fmt.Println", color: "#6f42c1" },
      { text: "go", color: "#005cc5" },
      { text: "interface{}", color: "#e36209" },

      // Rust
      { text: "fn", color: "#d73a49" },
      { text: "mut", color: "#d73a49" },
      { text: "impl", color: "#005cc5" },
      { text: "unwrap()", color: "#6f42c1" },
      { text: "match", color: "#d73a49" },
      { text: "trait", color: "#e36209" },
      { text: "Result<T, E>", color: "#22863a" },
      { text: "&mut", color: "#d73a49" },

      // C++
      { text: "std::cout", color: "#6f42c1" },
      { text: "#include", color: "#d73a49" },
      { text: "constexpr", color: "#d73a49" },
      { text: "std::vector", color: "#22863a" },
      { text: "auto", color: "#005cc5" },
      { text: "template<T>", color: "#e36209" },
      { text: "public:", color: "#d73a49" }
    ];
    
    class FloatingWord {
      constructor(x, y, directionX, directionY, size, wordObj) {
        this.x = x;
        this.y = y;
        this.directionX = directionX;
        this.directionY = directionY;
        this.size = size;
        this.text = wordObj.text;
        this.color = wordObj.color;
        this.opacity = (Math.random() * 0.10) + 0.03; // Very faint base opacity
        this.baseOpacity = this.opacity;
        this.density = (Math.random() * 20) + 1;
      }
      
      draw() {
        ctx.font = `bold ${this.size}px "Mulish", monospace`;
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fillText(this.text, this.x, this.y);
        ctx.globalAlpha = 1.0; // Reset
      }
      
      explode() {
        ctx.font = `bold ${this.size}px "Mulish", monospace`;
        let currentX = this.x;
        for (let i = 0; i < this.text.length; i++) {
          let char = this.text[i];
          let charWidth = ctx.measureText(char).width;
          
          // Explode outwards in a random direction
          let angle = Math.random() * Math.PI * 2;
          let speed = (Math.random() * 4) + 1;
          let vX = Math.cos(angle) * speed;
          let vY = Math.sin(angle) * speed;
          
          shrapnelArray.push({
            x: currentX,
            y: this.y,
            vX: vX,
            vY: vY,
            char: char,
            size: this.size,
            color: this.color,
            opacity: 1.0,
            rotation: 0,
            rotationSpeed: (Math.random() - 0.5) * 0.2
          });
          
          currentX += charWidth;
        }
      }
      
      update() {
        let textWidth = ctx.measureText(this.text).width;
        let leftBoundary = 80;
        
        // Edge bouncing
        if (this.x + textWidth + this.directionX > canvas.width || this.x + this.directionX < leftBoundary) {
          this.directionX *= -1;
        }
        if (this.y + this.directionY > canvas.height || this.y + this.directionY < 0) {
          this.directionY *= -1;
        }
        
        let cx = this.x + textWidth / 2;
        let cy = this.y - this.size / 2; 
        
        let isInsideObstacle = false;
        let willHitObstacle = false;
        let hitRect = null;
        let pad = 35; // Physical padding wall around elements
        
        for (let i = 0; i < obstacles.length; i++) {
          let r = obstacles[i];
          // If trapped inside an obstacle (e.g. user scrolled over it)
          if (cx > r.left - 10 && cx < r.right + 10 && cy > r.top - 10 && cy < r.bottom + 10) {
            isInsideObstacle = true;
            break;
          }
          // Predict if next step hits an obstacle padding wall
          let nextCx = cx + this.directionX;
          let nextCy = cy + this.directionY;
          if (nextCx > r.left - pad && nextCx < r.right + pad && nextCy > r.top - pad && nextCy < r.bottom + pad) {
            willHitObstacle = true;
            hitRect = r;
            break;
          }
        }
        
        if (isInsideObstacle) {
          // Fade out quickly and mark for removal if stuck behind text
          this.opacity -= 0.1;
          if (this.opacity <= 0) return true;
          this.x += this.directionX;
          this.y += this.directionY;
        } else {
          // Bounce logic
          if (willHitObstacle && hitRect) {
            if (cx <= hitRect.left - pad || cx >= hitRect.right + pad) {
              this.directionX *= -1;
            } else if (cy <= hitRect.top - pad || cy >= hitRect.bottom + pad) {
              this.directionY *= -1;
            } else {
              this.directionX *= -1;
              this.directionY *= -1;
            }
          }
          
          let dx = mouse.x - cx;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          
          if (mouse.x !== null && mouse.y !== null && distance < mouse.radius) {
            if (distance < mouse.radius * 0.5) {
              this.explode();
              return true; 
            }
            
            this.opacity = 0.35;
            let forceDirectionX = dx / distance;
            let forceDirectionY = dy / distance;
            let force = (mouse.radius - distance) / mouse.radius;
            let pushX = forceDirectionX * force * this.density * 0.15;
            let pushY = forceDirectionY * force * this.density * 0.15;
            
            // Only push away from mouse if it doesn't push into an obstacle
            let pushedCx = cx - pushX;
            let pushedCy = cy - pushY;
            let pushedHit = false;
            for (let i = 0; i < obstacles.length; i++) {
               let r = obstacles[i];
               if (pushedCx > r.left - pad && pushedCx < r.right + pad && pushedCy > r.top - pad && pushedCy < r.bottom + pad) {
                  pushedHit = true; break;
               }
            }
            if (!pushedHit) {
               this.x -= pushX;
               this.y -= pushY;
            }
          } else {
            if(this.opacity > this.baseOpacity) {
              this.opacity -= 0.015;
            }
            this.x += this.directionX;
            this.y += this.directionY;
          }
        }
        
        this.draw();
        return false;
      }
    }
    
    const init = () => {
      wordsArray = [];
      shrapnelArray = [];
      let numberOfWords = Math.min(60, (canvas.height * canvas.width) / 15000);
      for (let i = 0; i < numberOfWords; i++) {
        spawnWord();
      }
    };
    
    const spawnWord = () => {
      let size = (Math.random() * 15) + 10;
      let leftBoundary = 80;
      let x, y, isTrapped, attempts = 0;
      // Prevent spawning directly behind text
      do {
         x = (Math.random() * (canvas.width - leftBoundary - 100)) + leftBoundary;
         y = (Math.random() * (canvas.height - 50));
         isTrapped = false;
         for (let i = 0; i < obstacles.length; i++) {
           let r = obstacles[i];
           if (x > r.left - 40 && x < r.right + 40 && y > r.top - 40 && y < r.bottom + 40) {
             isTrapped = true; break;
           }
         }
         attempts++;
      } while (isTrapped && attempts < 10);
      
      let directionX = (Math.random() * 1) - 0.5;
      let directionY = (Math.random() * 1) - 0.5;
      let randomWord = syntaxWords[Math.floor(Math.random() * syntaxWords.length)];
      wordsArray.push(new FloatingWord(x, y, directionX, directionY, size, randomWord));
    };
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      init();
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw normal words
      for (let i = wordsArray.length - 1; i >= 0; i--) {
        let shouldRemove = wordsArray[i].update();
        if (shouldRemove) {
          wordsArray.splice(i, 1);
          // Respawn a new word to keep the screen populated
          spawnWord();
        }
      }
      
      // Update and draw exploding shrapnel (characters)
      for (let i = shrapnelArray.length - 1; i >= 0; i--) {
        let s = shrapnelArray[i];
        ctx.save();
        ctx.font = `bold ${s.size}px "Mulish", monospace`;
        ctx.fillStyle = s.color;
        ctx.globalAlpha = Math.max(0, s.opacity);
        
        // Translate to rotate around center of char
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rotation);
        ctx.fillText(s.char, 0, 0);
        
        ctx.restore();
        
        s.x += s.vX;
        s.y += s.vY;
        s.rotation += s.rotationSpeed;
        s.opacity -= 0.02; // fade out speed
        
        if (s.opacity <= 0) {
          shrapnelArray.splice(i, 1);
        }
      }
    };
    
    animate();
    
    return () => {
      clearInterval(obstacleInterval);
      window.removeEventListener('scroll', updateObstacles);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('click', handleTouchOrClick);
      window.removeEventListener('touchstart', handleTouchOrClick);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="network-bg">
      <canvas ref={canvasRef}></canvas>
    </div>
  );
};

export default InteractiveBackground;
